import { CardMedia, IconButton, Stack, Typography } from '@mui/material';
import { bookCard } from './Card.style';
import { pxToRem } from '~/theme/typography';
import {
  BASE_BUTTON_COLOR,
  BASE_BUTTON_VARIANT,
  BaseButton,
} from '~/components/Global/Button';
import FolderOpenSvg from '/assets/image/folder_open.svg';
import DeleteSvg from '/assets/image/delete.svg';
import { useFile } from '~/utils/hook/useFile';
import { useDownload } from '~/utils/hook/useDownload';
import { FileCollection } from '~/types/enum';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '~/store';
import { getterFileCatalog } from '~/store/file/fileGetter';
import { TFileCatalogDetail } from '~/store/file/file.type';
import { ConfirmDialog } from '~/components/Global/Dialog/ConfirmDialog';

type TProps = {
  fileName: string;
  img: string;
  exePath: string;
  type: FileCollection;
  folderPath: string;
  onOpen?: () => void;
};

export const FileCard = ({
  fileName,
  img,
  exePath,
  type,
  folderPath,
  onOpen,
}: TProps) => {
  const { openOneBook, openFlash, openPath, openTiku, deleteDiskFile } =
    useFile();
  const { deleteFile } = useDownload();
  const fileCatalog = useSelector((state: RootState) =>
    getterFileCatalog(state),
  );
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleOpen = useCallback(() => {
    if (type === FileCollection.onebook) {
      openOneBook(exePath);
    }
    if (type === FileCollection.flash) {
      openFlash(exePath);
    }
    if (type === FileCollection.tiku) {
      openTiku(exePath);
    }
    onOpen?.();
  }, [type, exePath, onOpen]);
  // 刪除時優先使用 folderPath；若為空（打包後 getter 可能傳空）則從 exePath 推導目錄
  // 如果 exePath 是空的，就回傳空字串
  const pathToDelete =
    folderPath && folderPath.trim() !== ''
      ? folderPath
      : exePath
        ? exePath.replace(/[/\\][^/\\]+$/, '')
        : '';

  // 確認刪除：同時刪除「磁碟上的安裝檔」與「下載檔」(比照下載紀錄頁面的刪除)
  const handleConfirmDelete = useCallback(() => {
    // 1. 刪磁碟上解壓後的安裝檔
    if (pathToDelete) {
      deleteDiskFile(pathToDelete);
    } else {
      console.error('[delete] pathToDelete is empty! Cannot delete disk file.');
    }
    // 2. 找出對應的下載記錄，比照下載紀錄刪除下載檔
    //    (deleteFile 會刪下載的 iso/zip、移除 downloadList 與 fileCatalog 項目，
    //     使「取得資源」該電子書按鈕回復為「下載」狀態)
    //    對應順序：
    //    a. 優先用 installedPath（解壓完成時寫回的安裝資料夾路徑）精準比對
    //    b. 以正規化檔名補上同書的其他分卷紀錄（-1/-2 可能只有第一卷寫到 installedPath）
    //    c. 舊紀錄沒有 installedPath 時，fallback 用書名比對（去副檔名與結尾分卷編號）；
    //       比對名同時取「紀錄的 fileName」與「URL 檔名」——舊紀錄的 fileName 是顯示名
    //       （例：公民2下_112南一電子書），與安裝資料夾名（112國中公民2下電子書，
    //       依 URL 檔名命名）不同，需靠 URL 檔名才能對上
    const normalizeName = (name: string) =>
      (name || '')
        .replace(/\.[^.]+$/, '') // 去副檔名
        .replace(/-\d+$/, '') // 去結尾分卷編號
        .trim();
    const normalizePath = (p: string) =>
      (p || '').replace(/\//g, '\\').replace(/\\+$/, '').toLowerCase();
    const urlBaseName = (url?: string) => {
      if (!url) return '';
      try {
        return normalizeName(decodeURI(url).split('/').pop() || '');
      } catch {
        return normalizeName(url.split('/').pop() || '');
      }
    };
    const candidateNames = (item: TFileCatalogDetail) =>
      [normalizeName(item.fileName), urlBaseName(item.url)].filter(Boolean);
    const entries = Object.values(fileCatalog);
    const targetPath = normalizePath(pathToDelete);
    const primary = targetPath
      ? entries.filter(
          item =>
            item.installedPath &&
            normalizePath(item.installedPath) === targetPath,
        )
      : [];
    const nameKeys = new Set(
      (primary.length > 0
        ? primary.flatMap(item => candidateNames(item))
        : [normalizeName(fileName)]
      ).filter(Boolean),
    );
    const deleted = new Set<string>();
    entries.forEach(item => {
      const isMatched =
        primary.includes(item) ||
        candidateNames(item).some(name => nameKeys.has(name));
      if (isMatched && item.filePath && !deleted.has(item.filePath)) {
        deleted.add(item.filePath);
        deleteFile({
          filePath: item.filePath,
          filename: item.fileName,
        });
      }
    });
    setConfirmOpen(false);
  }, [pathToDelete, fileCatalog, fileName, deleteDiskFile, deleteFile]);

  // Local 開啟題庫 => 會出現「無效的類別字串, ProgID: "Word.Application"」錯誤，但還是會正常出現題庫程式
  return (
    <>
      <bookCard.Container>
        <bookCard.Head title={fileName} />
        <bookCard.Content>
          <CardMedia
            component="img"
            src={img}
            sx={{ height: '238px', aspectRatio: '209/238' }}
          />
        </bookCard.Content>
        <bookCard.Action>
          <BaseButton
            variant={BASE_BUTTON_VARIANT.GHOST}
            color={BASE_BUTTON_COLOR.SUCCESS}
            text="開啟"
            onClick={handleOpen}
            width="96px"
            style={{ fontSize: pxToRem(18) }}
          />
          <IconButton
            onClick={() => {
              openPath(folderPath || pathToDelete);
            }}
          >
            <img src={FolderOpenSvg} alt="FolderOpenSvg" />
          </IconButton>
          <IconButton onClick={() => setConfirmOpen(true)}>
            <img src={DeleteSvg} alt="DeleteSvg" />
          </IconButton>
        </bookCard.Action>
      </bookCard.Container>
      <ConfirmDialog
        open={confirmOpen}
        title={fileName}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
      >
        <Stack spacing={1}>
          <Typography sx={{ fontWeight: 700 }}>確定要刪除此項目嗎？</Typography>
          <Typography>
            刪除後，這項內容會從這台電腦移除；如需使用，可再重新下載。
          </Typography>
        </Stack>
      </ConfirmDialog>
    </>
  );
};
